<template>
  <generic-modal :title="$t('appraisal details')" ref="generic_modal">
    <div class="columns">
      <div class="column">
        <div class="field">
          <label class="label">{{ $t("property address") }}</label>
          <div class="control">
            <p class="content">{{ meta.property_address }}</p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("property type") }}</label>
          <div class="control">
            <p class="content">
              {{ meta.property_type_name || $t("not specified") }}
            </p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("price per sqm") }}</label>
          <div class="control">
            <p class="content">
              <span class="has-text-weight-bold has-text-success">
                {{ format_currency(meta.price_per_sqm) }}
              </span>
            </p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("total area") }}</label>
          <div class="control">
            <p class="content">{{ meta.total_area }} m²</p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("total value") }}</label>
          <div class="control">
            <p class="content">
              <span class="has-text-weight-bold has-text-primary">
                {{ format_currency(meta.total_value) }}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div class="column">
        <div class="field">
          <label class="label">{{ $t("valuation date") }}</label>
          <div class="control">
            <p class="content">{{ format_date(meta.valuation_date) }}</p>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("status") }}</label>
          <div class="control">
            <span
              class="tag"
              :class="{
                'is-success': meta.status === 'active',
                'is-danger': meta.status === 'inactive',
                'is-warning': meta.status === 'pending',
              }"
            >
              {{ $t(meta.status) }}
            </span>
          </div>
        </div>

        <div class="field" v-if="meta.latitude && meta.longitude">
          <label class="label">{{ $t("location") }}</label>
          <div class="control">
            <div class="content">
              <p>
                <strong>{{ $t("latitude") }}:</strong> {{ meta.latitude }}<br />
                <strong>{{ $t("longitude") }}:</strong> {{ meta.longitude }}
              </p>
              <!-- Mini map -->
              <div
                id="detail-map"
                style="height: 200px; width: 100%"
                ref="mapContainer"
              ></div>
            </div>
          </div>
        </div>

        <div class="field">
          <label class="label">{{ $t("created") }}</label>
          <div class="control">
            <p class="content">
              {{ format_date(meta.created_at) }}<br />
              <small>{{ $t("by") }}: {{ meta.created_by }}</small>
            </p>
          </div>
        </div>

        <div class="field" v-if="meta.updated_at">
          <label class="label">{{ $t("updated") }}</label>
          <div class="control">
            <p class="content">
              {{ format_date(meta.updated_at) }}<br />
              <small>{{ $t("by") }}: {{ meta.updated_by }}</small>
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Photos section -->
    <div class="field" v-if="photos.length > 0">
      <label class="label">{{ $t("photos") }}</label>
      <div class="control">
        <div class="columns is-multiline">
          <div class="column is-3" v-for="photo in photos" :key="photo.uuid">
            <div class="card">
              <div class="card-image">
                <figure class="image is-4by3">
                  <img
                    :src="get_photo_url(photo.file_path)"
                    :alt="photo.description || 'Property photo'"
                    @click="show_photo_modal(photo)"
                    style="cursor: pointer"
                  />
                </figure>
              </div>
              <div class="card-content" v-if="photo.description">
                <div class="content">
                  <p class="is-size-7">{{ photo.description }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="field" v-if="meta.notes">
      <label class="label">{{ $t("notes") }}</label>
      <div class="control">
        <div class="content">
          <p>{{ meta.notes }}</p>
        </div>
      </div>
    </div>

    <!-- Photo modal -->
    <div class="modal" :class="{ 'is-active': photo_modal_active }">
      <div class="modal-background" @click="close_photo_modal"></div>
      <div class="modal-content">
        <p class="image">
          <img :src="selected_photo_url" :alt="selected_photo_description" />
        </p>
      </div>
      <button
        class="modal-close is-large"
        aria-label="close"
        @click="close_photo_modal"
      ></button>
    </div>

    <!-- Error handling -->
    <div class="notification is-danger mt-5" v-if="error">
      <p v-html="error"></p>
    </div>

    <!-- Footer with v-slot:footer pattern -->
    <template v-slot:footer>
      <button class="button is-primary" @click="edit_appraisal">
        <span class="icon"><i class="fa fa-edit"></i></span>
        <span>{{ $t("edit") }}</span>
      </button>
      <button class="button" @click="close_modal">
        <span class="icon"><i class="fa fa-close"></i></span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import {
  get,
  generate_error_message_text,
  format_currency,
  format_date,
} from "@/utils";
import GenericModal from "@/components/generic_modal/Index.vue";
import L from "leaflet";

export default {
  name: "AppraisalDetailModal",
  components: {
    GenericModal,
  },
  data() {
    return {
      meta: {
        property_address: "",
        property_type_name: "",
        price_per_sqm: 0,
        total_area: 0,
        total_value: 0,
        latitude: null,
        longitude: null,
        valuation_date: "",
        status: "",
        notes: "",
        created_at: "",
        created_by: "",
        updated_at: "",
        updated_by: "",
        uuid: null,
      },
      photos: [],
      photo_modal_active: false,
      selected_photo_url: "",
      selected_photo_description: "",
      error: "",
      map: null,
    };
  },
  methods: {
    show_modal(uuid) {
      let v = this;
      v.entity_uuid = uuid;
      v.set_initial_state();
      v.get_appraisal(uuid);
      v.$refs.generic_modal.show_modal();
    },

    close_modal() {
      this.$refs.generic_modal.close_modal();
      this.destroy_map();
      this.set_initial_state();
    },

    get_appraisal(uuid) {
      let v = this;
      get("/appraisal/appraisal", { uuid })
        .then((appraisal) => {
          v.meta = { ...v.meta, ...appraisal };

          // Calculate total value
          if (v.meta.price_per_sqm && v.meta.total_area) {
            v.meta.total_value = v.meta.price_per_sqm * v.meta.total_area;
          }

          // Initialize map if coordinates exist
          v.$nextTick(() => {
            if (v.meta.latitude && v.meta.longitude) {
              v.init_map();
            }
          });

          // Load photos
          v.load_photos();
        })
        .catch((error) => {
          v.error = generate_error_message_text(error);
        });
    },

    init_map() {
      let v = this;

      // Wait for element to be available
      if (!v.$refs.mapContainer) {
        return;
      }

      try {
        // Destroy existing map
        v.destroy_map();

        // Create new map
        v.map = L.map(v.$refs.mapContainer).setView(
          [v.meta.latitude, v.meta.longitude],
          15
        );

        // Add tile layer
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap contributors",
        }).addTo(v.map);

        // Add marker for the property
        L.marker([v.meta.latitude, v.meta.longitude])
          .addTo(v.map)
          .bindPopup(
            `<strong>${v.meta.property_address}</strong><br>
             ${v.$t("price per sqm")}: ${v.format_currency(
              v.meta.price_per_sqm
            )}<br>
             ${v.$t("total area")}: ${v.meta.total_area} m²`
          );
      } catch (error) {
        console.error("Error initializing map:", error);
      }
    },

    destroy_map() {
      if (this.map) {
        this.map.remove();
        this.map = null;
      }
    },

    load_photos() {
      let v = this;
      if (!v.meta.uuid) return;

      get("/appraisal/photo", { appraisal_uuid: v.meta.uuid })
        .then((photos) => {
          v.photos = photos;
        })
        .catch((error) => {
          console.error("Error loading photos:", error);
        });
    },

    get_photo_url(file_path) {
      return file_path;
    },

    show_photo_modal(photo) {
      this.selected_photo_url = this.get_photo_url(photo.file_path);
      this.selected_photo_description = photo.description || "Property photo";
      this.photo_modal_active = true;
    },

    close_photo_modal() {
      this.photo_modal_active = false;
      this.selected_photo_url = "";
      this.selected_photo_description = "";
    },

    edit_appraisal() {
      this.$emit("edit", this.meta.uuid);
      this.close_modal();
    },

    set_initial_state() {
      this.meta = {
        property_address: "",
        property_type_name: "",
        price_per_sqm: 0,
        total_area: 0,
        total_value: 0,
        latitude: null,
        longitude: null,
        valuation_date: "",
        status: "",
        notes: "",
        created_at: "",
        created_by: "",
        updated_at: "",
        updated_by: "",
        uuid: null,
      };
      this.photos = [];
      this.photo_modal_active = false;
      this.selected_photo_url = "";
      this.selected_photo_description = "";
      this.error = "";
    },

    format_currency,
    format_date,
  },

  beforeDestroy() {
    this.destroy_map();
  },
};
</script>

<style scoped>
.content {
  margin-bottom: 0.5rem;
}

.tag {
  font-weight: bold;
}

#detail-map {
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-top: 0.5rem;
}

.card-image .image {
  overflow: hidden;
}

.card-image .image img {
  object-fit: cover;
  width: 100%;
  height: 100%;
  transition: transform 0.2s;
}

.card-image .image img:hover {
  transform: scale(1.05);
}

.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.modal-content .image img {
  max-height: 80vh;
  max-width: 100%;
  object-fit: contain;
}
</style>
